import React, { useRef, useEffect, useCallback } from 'react';

interface ReportRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * 大报告专用富文本编辑器
 * - 支持 Word 复制粘贴（保留格式）
 * - 支持指标占位符插入和管理
 * - 更大的编辑区域
 */
const ReportRichTextEditor: React.FC<ReportRichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // 初始化内容
  useEffect(() => {
    if (editorRef.current && isInitialMount.current) {
      editorRef.current.innerHTML = value || '';
      isInitialMount.current = false;
    }
  }, []);

  // 外部值变化时更新（非用户输入时）
  useEffect(() => {
    if (editorRef.current && !isInitialMount.current) {
      const currentContent = editorRef.current.innerHTML;
      if (currentContent !== value && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, cmdValue?: string) => {
    document.execCommand(command, false, cmdValue);
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
    }
  };

  /**
   * HTML 白名单过滤 - 用于 Word 粘贴内容清洗
   */
  const sanitizeHtml = (html: string): string => {
    // 移除 Word 特有的 XML 和条件注释
    let clean = html
      .replace(/<\!--\[if[^>]*>[\s\S]*?<\!\[endif\]-->/gi, '')
      .replace(/<\!--[\s\S]*?-->/g, '')
      .replace(/<xml[\s\S]*?<\/xml>/gi, '')
      .replace(/<o:[\s\S]*?<\/o:[^>]*>/gi, '')
      .replace(/<w:[\s\S]*?<\/w:[^>]*>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      .replace(/<link[^>]*>/gi, '');

    // 创建临时 DOM 来过滤
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = clean;

    // 允许的标签白名单
    const allowedTags = new Set([
      'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'STRONG', 'B', 'EM', 'I', 'U', 'S',
      'SPAN', 'DIV', 'BR',
      'UL', 'OL', 'LI',
      'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
      'A', 'BLOCKQUOTE', 'PRE', 'CODE',
      'SUB', 'SUP',
    ]);

    // 允许的属性白名单
    const allowedAttributes = new Set([
      'style', 'class', 'href', 'target',
      'colspan', 'rowspan',
      'data-placeholder-key', 'contenteditable',
    ]);

    // 允许的 style 属性
    const allowedStyles = new Set([
      'color', 'background-color', 'font-size', 'font-weight',
      'font-style', 'text-decoration', 'text-align',
      'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
      'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
      'border', 'border-collapse', 'width', 'height',
      'line-height', 'list-style-type',
    ]);

    const cleanNode = (node: Node): void => {
      const childNodes = Array.from(node.childNodes);
      for (const child of childNodes) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement;
          if (!allowedTags.has(el.tagName)) {
            // 将不允许的标签替换为其内容
            while (el.firstChild) {
              el.parentNode?.insertBefore(el.firstChild, el);
            }
            el.parentNode?.removeChild(el);
          } else {
            // 过滤属性
            const attrs = Array.from(el.attributes);
            for (const attr of attrs) {
              if (!allowedAttributes.has(attr.name)) {
                el.removeAttribute(attr.name);
              }
            }
            // 过滤 style 属性中的 mso-* 等
            if (el.style.cssText) {
              const styles = el.style.cssText.split(';').filter(s => {
                const prop = s.split(':')[0]?.trim().toLowerCase();
                return prop && allowedStyles.has(prop);
              });
              el.setAttribute('style', styles.join(';'));
              if (!el.getAttribute('style')) {
                el.removeAttribute('style');
              }
            }
            cleanNode(el);
          }
        }
      }
    };

    cleanNode(tempDiv);
    return tempDiv.innerHTML;
  };

  /**
   * 粘贴处理 - 支持 Word HTML 粘贴
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    if (html) {
      // 有 HTML 内容 (Word 复制), 清洗后插入
      const cleaned = sanitizeHtml(html);
      document.execCommand('insertHTML', false, cleaned);
    } else if (text) {
      // 纯文本
      document.execCommand('insertText', false, text);
    }
  };

  /**
   * 在光标位置插入指标占位符
   * @param isChart 是否为图表类占位符（独占一行，更大显示区域）
   */
  const insertPlaceholder = useCallback((key: string, displayName: string, isChart?: boolean) => {
    if (!editorRef.current) return;

    editorRef.current.focus();

    let placeholderHtml: string;
    if (isChart) {
      // 图表占位符：独占一行的块级显示
      placeholderHtml =
        `<div class="report-placeholder report-chart-placeholder" data-placeholder-key="${key}" contenteditable="false" style="display:block;background:#eff6ff;border:2px dashed #93c5fd;border-radius:12px;padding:24px;color:#1d4ed8;font-size:0.95em;cursor:default;user-select:all;margin:12px 0;text-align:center;">` +
        `<span style="font-size:1.5em;display:block;margin-bottom:4px;">${key.includes('PIE') ? '🥧' : '📊'}</span>` +
        `{{${displayName}}}` +
        `</div><p><br></p>`;
    } else {
      // 文本占位符：行内显示
      placeholderHtml =
        `<span class="report-placeholder" data-placeholder-key="${key}" contenteditable="false" style="display:inline-block;background:#dbeafe;border:1px solid #93c5fd;border-radius:6px;padding:1px 8px;color:#1d4ed8;font-size:0.9em;cursor:default;user-select:all;margin:0 2px;">` +
        `{{${displayName}}}` +
        `</span>\u200B`;
    }

    document.execCommand('insertHTML', false, placeholderHtml);

    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  /**
   * 处理键盘事件 - 占位符删除
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const node = e.key === 'Backspace'
        ? range.startContainer.previousSibling || range.startContainer.parentElement
        : range.startContainer.nextSibling || range.startContainer.parentElement;

      if (node && node instanceof HTMLElement && node.classList.contains('report-placeholder')) {
        e.preventDefault();
        node.remove();
        if (editorRef.current) {
          onChange(editorRef.current.innerHTML);
        }
      }
    }
  };

  // 暴露 insertPlaceholder 方法给父组件
  // 通过在 window 上挂载临时方法（因为 contentEditable 的 ref forwarding 比较复杂）
  useEffect(() => {
    (window as any).__reportEditorInsertPlaceholder = insertPlaceholder;
    return () => {
      delete (window as any).__reportEditorInsertPlaceholder;
    };
  }, [insertPlaceholder]);

  return (
    <div className="border border-gray-300 rounded-2xl overflow-hidden bg-white shadow-soft">
      {/* 工具栏 */}
      <div className="border-b border-gray-200 bg-gray-50 p-2 flex flex-wrap gap-1">
        {/* 标题 */}
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h1>')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
          title="一级标题"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
          title="二级标题"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h3>')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 font-medium"
          title="三级标题"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<p>')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="正文"
        >
          P
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 格式 */}
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1.5 text-sm font-bold border border-gray-300 rounded-lg hover:bg-gray-100"
          title="粗体"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1.5 text-sm italic border border-gray-300 rounded-lg hover:bg-gray-100"
          title="斜体"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1.5 text-sm underline border border-gray-300 rounded-lg hover:bg-gray-100"
          title="下划线"
        >
          U
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 对齐 */}
        <button
          type="button"
          onClick={() => execCommand('justifyLeft')}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="左对齐"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>format_align_left</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyCenter')}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="居中"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>format_align_center</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyRight')}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="右对齐"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>format_align_right</span>
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 列表 */}
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="无序列表"
        >
          &bull; 列表
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="有序列表"
        >
          1. 列表
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        {/* 颜色和表格 */}
        <button
          type="button"
          onClick={() => {
            const color = prompt('输入颜色（如：#ff0000 或 red）：');
            if (color) execCommand('foreColor', color);
          }}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="文字颜色"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>palette</span> 颜色
        </button>
        <button
          type="button"
          onClick={() => {
            const rows = prompt('行数：', '3');
            const cols = prompt('列数：', '3');
            if (rows && cols) {
              let table = '<table style="border-collapse:collapse;width:100%;margin:8px 0;">';
              for (let r = 0; r < parseInt(rows); r++) {
                table += '<tr>';
                for (let c = 0; c < parseInt(cols); c++) {
                  const tag = r === 0 ? 'th' : 'td';
                  table += `<${tag} style="border:1px solid #d1d5db;padding:8px;">${r === 0 ? '标题' : '内容'}</${tag}>`;
                }
                table += '</tr>';
              }
              table += '</table>';
              document.execCommand('insertHTML', false, table);
              if (editorRef.current) {
                editorRef.current.focus();
                onChange(editorRef.current.innerHTML);
              }
            }
          }}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
          title="插入表格"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>table_chart</span> 表格
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 text-red-500"
          title="清除格式"
        >
          清除格式
        </button>
      </div>

      {/* 编辑区域 */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        className="p-6 min-h-[500px] max-h-[700px] overflow-y-auto focus:outline-none prose prose-sm max-w-none"
        data-placeholder={placeholder || '在此编辑报告内容，可从 Word 复制粘贴...'}
        style={{ caretColor: 'black' }}
      />

      <style>
        {`
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
            display: block;
          }

          [contenteditable] h1 {
            font-size: 1.875em;
            font-weight: bold;
            margin: 0.67em 0;
          }

          [contenteditable] h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin: 0.5em 0;
          }

          [contenteditable] h3 {
            font-size: 1.25em;
            font-weight: bold;
            margin: 0.5em 0;
          }

          [contenteditable] p {
            margin: 0.5em 0;
          }

          [contenteditable] ul,
          [contenteditable] ol {
            margin: 0.5em 0;
            padding-left: 2em;
          }

          [contenteditable] strong,
          [contenteditable] b {
            font-weight: bold;
          }

          [contenteditable] em,
          [contenteditable] i {
            font-style: italic;
          }

          [contenteditable] u {
            text-decoration: underline;
          }

          [contenteditable] table {
            border-collapse: collapse;
            width: 100%;
            margin: 8px 0;
          }

          [contenteditable] td,
          [contenteditable] th {
            border: 1px solid #d1d5db;
            padding: 8px;
          }

          [contenteditable] th {
            background-color: #f3f4f6;
            font-weight: bold;
          }

          [contenteditable] blockquote {
            border-left: 3px solid #d1d5db;
            padding-left: 16px;
            margin: 8px 0;
            color: #6b7280;
          }

          .report-placeholder {
            display: inline-block;
            background: #dbeafe;
            border: 1px solid #93c5fd;
            border-radius: 6px;
            padding: 1px 8px;
            color: #1d4ed8;
            font-size: 0.9em;
            cursor: default;
            user-select: all;
            margin: 0 2px;
          }

          .report-placeholder:hover {
            background: #bfdbfe;
            border-color: #60a5fa;
          }

          .report-chart-placeholder {
            display: block !important;
            background: #eff6ff !important;
            border: 2px dashed #93c5fd !important;
            border-radius: 12px !important;
            padding: 24px !important;
            text-align: center;
            margin: 12px 0 !important;
          }

          .report-chart-placeholder:hover {
            background: #dbeafe !important;
            border-color: #60a5fa !important;
          }
        `}
      </style>
    </div>
  );
};

export default ReportRichTextEditor;
